/**
 * The tab's View-as session id, in sessionStorage (M4S-5670,
 * docs/pdlc/cs-view-as-client.md §4).
 *
 * Holds `{ v: 1, staffUserId, sessionId }` and nothing else. The client is
 * never read from here: it always comes back from the server's resume, so a
 * hand-edited entry can at most name a session id the server then refuses.
 * sessionStorage is per tab, so a new tab starts as the staff member and a
 * duplicated tab keeps the same session.
 *
 * Every access is wrapped: storage that throws (private mode, blocked site
 * data) reads as "no View-as".
 */

import { VIEW_AS_END_REASON_KEY, VIEW_AS_STORAGE_KEY } from '@/lib/view-as/constants';

export interface ViewAsTabEntry {
    v: 1;
    staffUserId: string;
    sessionId: string;
}

function storage(): Storage | null {
    try {
        return typeof window === 'undefined' ? null : window.sessionStorage;
    } catch {
        return null;
    }
}

/**
 * The tab's entry for this staff member. An entry written by anyone else (a
 * different sign-in in the same tab) is dropped and reads as none.
 */
export function readViewAsEntry(staffUserId: string): ViewAsTabEntry | null {
    const store = storage();
    if (!store) return null;
    try {
        const raw = store.getItem(VIEW_AS_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<ViewAsTabEntry>;
        const valid =
            parsed?.v === 1 &&
            typeof parsed.sessionId === 'string' &&
            parsed.sessionId.length > 0 &&
            typeof parsed.staffUserId === 'string';
        if (!valid || parsed.staffUserId !== staffUserId) {
            store.removeItem(VIEW_AS_STORAGE_KEY);
            return null;
        }
        return {
            v: 1,
            staffUserId: parsed.staffUserId as string,
            sessionId: parsed.sessionId as string,
        };
    } catch {
        try {
            store.removeItem(VIEW_AS_STORAGE_KEY);
        } catch {
            // nothing more to do
        }
        return null;
    }
}

/** Returns whether the entry was written; a start that cannot persist does not reload. */
export function writeViewAsEntry(staffUserId: string, sessionId: string): boolean {
    const store = storage();
    if (!store) return false;
    try {
        const entry: ViewAsTabEntry = { v: 1, staffUserId, sessionId };
        store.setItem(VIEW_AS_STORAGE_KEY, JSON.stringify(entry));
        return true;
    } catch {
        return false;
    }
}

export function clearViewAsEntry(): void {
    try {
        storage()?.removeItem(VIEW_AS_STORAGE_KEY);
    } catch {
        // nothing more to do
    }
}

/** Leave a one-shot reason for the notice shown after the end path's reload. */
export function writeViewAsEndReason(reason: string): void {
    try {
        storage()?.setItem(VIEW_AS_END_REASON_KEY, reason);
    } catch {
        // the reload still happens; only the notice is lost
    }
}

/** Read and remove the one-shot end reason, so the notice shows once. */
export function takeViewAsEndReason(): string | null {
    const store = storage();
    if (!store) return null;
    try {
        const reason = store.getItem(VIEW_AS_END_REASON_KEY);
        if (reason != null) store.removeItem(VIEW_AS_END_REASON_KEY);
        return reason;
    } catch {
        return null;
    }
}
